/**
 * Download Helper Utility
 * Provides cross-browser compatible download functionality with mobile fallbacks
 */

/**
 * Detect if running on a mobile device
 */
function isMobileDevice(): boolean {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

/**
 * Detect if browser supports the download attribute
 */
function supportsDownloadAttribute(): boolean {
  const a = document.createElement('a');
  return typeof a.download !== 'undefined';
}

/**
 * Download a blob as a file with cross-browser compatibility
 * Includes special handling for mobile browsers and privacy browsers like DuckDuckGo
 * 
 * @param blob - The blob to download
 * @param filename - The filename for the downloaded file
 */
export function downloadBlob(blob: Blob, filename: string): void {
  // Try multiple methods in order of preference
  
  // Method 1: Try using msSaveBlob for IE/Edge Legacy
  if (typeof (window.navigator as any).msSaveBlob !== 'undefined') {
    (window.navigator as any).msSaveBlob(blob, filename);
    return;
  }

  // Method 2: Standard approach with download attribute
  // This works on most desktop browsers and some mobile browsers
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = filename;
  
  // Add to DOM
  document.body.appendChild(a);
  
  try {
    // For mobile browsers, especially DuckDuckGo, we need to:
    // 1. Set target="_blank" to ensure it opens
    // 2. Add a small delay before click
    // 3. Use setTimeout to ensure DOM is ready
    
    if (isMobileDevice() || !supportsDownloadAttribute()) {
      // Mobile-specific handling
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      
      // Small delay to ensure the browser processes the element
      setTimeout(() => {
        // Try to trigger download
        a.click();
        
        // Cleanup after a delay to ensure download starts
        setTimeout(() => {
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }, 100);
      }, 10);
    } else {
      // Desktop - standard approach
      a.click();
      
      // Cleanup
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  } catch (error) {
    // Fallback: Try opening in new window
    console.error('Download failed, trying fallback method:', error);
    
    try {
      // Create a new window with the blob URL
      const win = window.open(url, '_blank');
      if (!win) {
        // If popup blocked, try alternative
        window.location.href = url;
      }
      
      // Cleanup after delay
      setTimeout(() => {
        if (document.body.contains(a)) {
          document.body.removeChild(a);
        }
        URL.revokeObjectURL(url);
      }, 1000);
    } catch (fallbackError) {
      console.error('Fallback download method also failed:', fallbackError);
      
      // Final cleanup
      if (document.body.contains(a)) {
        document.body.removeChild(a);
      }
      URL.revokeObjectURL(url);
      
      // Show user message
      alert('Download failed. Your browser may be blocking downloads. Please check your browser settings.');
    }
  }
}

/**
 * Download using data URL (alternative method for small files)
 * Useful for very small images where blob URLs might fail
 * 
 * @param dataUrl - The data URL
 * @param filename - The filename for the downloaded file
 */
export function downloadDataUrl(dataUrl: string, filename: string): void {
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = dataUrl;
  a.download = filename;
  
  document.body.appendChild(a);
  
  if (isMobileDevice()) {
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    
    setTimeout(() => {
      a.click();
      setTimeout(() => {
        if (document.body.contains(a)) {
          document.body.removeChild(a);
        }
      }, 100);
    }, 10);
  } else {
    a.click();
    document.body.removeChild(a);
  }
}

/**
 * Convert a blob to data URL and download
 * Fallback method for browsers that don't support blob URLs well
 * 
 * @param blob - The blob to download
 * @param filename - The filename for the downloaded file
 */
export async function downloadBlobAsDataUrl(blob: Blob, filename: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = () => {
      try {
        const dataUrl = reader.result as string;
        downloadDataUrl(dataUrl, filename);
        resolve();
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read blob as data URL'));
    };
    
    reader.readAsDataURL(blob);
  });
}
